import React, { useState } from 'react';
import { Header } from './components/br24/header/Header';
import { Date } from './components/br24/date/Date';
import { HiddenFoldable } from './components/br24/foldable/Foldable';
import { mainConfig } from './config/config.provider';
import { Teaser } from './components/br24/relatedContent/RelatedContent';
import { RelatedContent } from './components/br24/relatedContent/styles.RelatedContent';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import { InfoBox } from './components/custom/infoBox/InfoBox';
import { DrawIt, DrawItResult, DrawItScore } from './components/custom/drawIt/DrawIt';
import { Article, Section, Block, H1, H2, H3, P, A, Strong, List, ListItem } from './styles/styledComponents';

export const Content = () => {
    const [totalScore, setTotalScore] = useState<DrawItScore[]>([]);

    return (
        <Article>
            <Header img={mainConfig.headerImage} isBig={false} hideAside={!mainConfig.showAside} />
            <HiddenFoldable triggerText={'Bild: BR24'}>
                Merkel nach Zahlen: Statistik zum selber schätzen und zeichnen
            </HiddenFoldable>

            <Section>
                <Block isOverlay={mainConfig.headerOverlay}>
                    <Date day={7} month={9} year={2021} time="07:55" />
                    {mainConfig.headerOverlay ? <H1>{mainConfig.headline}</H1> : null}
                    <P>
                        <Strong>
                            Staatsverschuldung, Asylanträge, Windkraft: Wie hat sich Deutsch­land unter Angela Merkels
                            Kanzlerschaft verändert? Zeichnen Sie das Diagramm und schätzen Sie die Werte.
                        </Strong>
                    </P>

                    <P>
                        Mit der Bundestagswahl am 26. September endet eine Ära: 16 Jahre lang stand Angela Merkel an der
                        Spitze Deutschlands. In vier Amtszeiten bestimmte sie maßgeblich die Politik in vielen Bereichen
                        dieses Landes. Machen Sie mit bei unserem interaktiven Quiz, schätzen Sie die Entwicklung
                        verschiedener Indikatoren und teilen Sie Ihr Ergebnis mit Freunden und Bekannten.
                    </P>
                </Block>

                <DrawIt
                    id="arbeitslosenquote"
                    question="Schätzen Sie: Unter Angela Merkel ist die Arbeitslosenquote …"
                    answer="… deutlich gesunken. Zwar wurde die positive Entwicklung durch die Folgen der Finanzkrise 2008 <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/wirtschaft/aufschwung100.html'>kurzzeitig gedämpft</a>, trotzdem lag die Quote lange Zeit auf dem niedrigsten Stand <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/wirtschaft/arbeitslosenzahlen-123.html'>seit der Wieder­vereinigung</a>. Erst mit Beginn der Corona-Pandemie gab es wieder einen leichten Anstieg der Arbeitslosigkeit in Deutschland. <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/wirtschaft/arbeitsmarkt-studie-es-geht-wieder-aufwaerts,SgWI5MK'>Erste Studien</a> deuten jedoch darauf hin, dass sich der Arbeitsmarkt langsam wieder erholt."
                    data={[
                        { year: 2002, value: 9.8 },
                        { year: 2003, value: 10.5 },
                        { year: 2004, value: 10.5 },
                        { year: 2005, value: 11.7 },
                        { year: 2006, value: 11 },
                        { year: 2007, value: 9 },
                        { year: 2008, value: 7.8 },
                        { year: 2009, value: 8.1 },
                        { year: 2010, value: 7.7 },
                        { year: 2011, value: 7.1 },
                        { year: 2012, value: 6.8 },
                        { year: 2013, value: 6.9 },
                        { year: 2014, value: 6.7 },
                        { year: 2015, value: 6.4 },
                        { year: 2016, value: 6.1 },
                        { year: 2017, value: 5.7 },
                        { year: 2018, value: 5.2 },
                        { year: 2019, value: 5 },
                        { year: 2020, value: 5.9 },
                        { year: 2021, value: 6.1 }
                    ]}
                    annotations={[
                        {
                            text: 'Finanzkrise',
                            year: 2009,
                            value: 8.1
                        },
                        {
                            text: 'Corona',
                            year: 2020,
                            value: 5.9
                        }
                    ]}
                    unit="%"
                    factor={1}
                    precision={1}
                    maxValue={20}
                    breakpoint={2005}
                    showHint={true}
                    totalScore={totalScore}
                    setTotalScore={setTotalScore}
                />

                <DrawIt
                    id="staatsverschuldung"
                    question="Wie hat sich die Staatsverschuldung entwickelt?"
                    answer="Infolge der Finanzkrise 2008 kletterte die Staatsverschuldung <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/wirtschaft/banken170.html'>auf ein Rekordniveau</a> von mehr als 2.000 Milliarden Euro. Die Politik der „Schwarzen Null“, welche vor allem durch Merkels ehemaligen Finanzminister Wolfgang Schäuble vorangetrieben wurde, führte anschließend zu einer <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/deutschland-welt/deutsche-staatsverschuldung-sinkt-unter-zwei-billionen-marke,QzYwqkE'>stetigen Schuldenreduzierung</a>. Mit Beginn der Corona-Pandemie und der damit verbundenen Notfallpakete wuchs der Schuldenberg erstmals seit 2013 wieder an und erreichte eine <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/wirtschaft/corona-schulden-kann-deutschland-sich-das-leisten,SJ8lqbX'>neue Höchstmarke</a>."
                    data={[
                        { year: 2002, value: 1318 },
                        { year: 2003, value: 1405 },
                        { year: 2004, value: 1475 },
                        { year: 2005, value: 1546 },
                        { year: 2006, value: 1595 },
                        { year: 2007, value: 1604 },
                        { year: 2008, value: 1673 },
                        { year: 2009, value: 1789 },
                        { year: 2010, value: 2115 },
                        { year: 2011, value: 2151 },
                        { year: 2012, value: 2229 },
                        { year: 2013, value: 2214 },
                        { year: 2014, value: 2216 },
                        { year: 2015, value: 2189 },
                        { year: 2016, value: 2172 },
                        { year: 2017, value: 2123 },
                        { year: 2018, value: 2074 },
                        { year: 2019, value: 2058 },
                        { year: 2020, value: 2325 }
                    ]}
                    annotations={[
                        {
                            text: 'Finanzkrise',
                            year: 2008,
                            value: 1673
                        },
                        {
                            text: '„Schwarze Null“',
                            year: 2013,
                            value: 2214
                        }
                    ]}
                    unit="Mrd. €"
                    factor={1}
                    precision={0}
                    maxValue={3000}
                    breakpoint={2005}
                    marginRight={120}
                    showHint={false}
                    totalScore={totalScore}
                    setTotalScore={setTotalScore}
                />

                <DrawIt
                    id="sozialwohnungen"
                    question="Wie hat sich die Zahl der Sozialwohnungen entwickelt?"
                    answer="Der Bestand an mietpreisgebundenen Sozialwohnungen hat während der Merkel-Jahre <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/deutschland-welt/deutschland-im-dilemma-zahl-der-sozialwohnungen-stark-gesunken,ScODNQB'>stetig abgenommen</a>. Existierten in Deutschland 2006 noch rund zwei Millionen Sozialmietwohnungen, reduzierte sich deren Bestand bis 2019 um fast die Hälfte. Einer der Hauptgründe für diese Entwicklung ist die <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachricht/inhalt/akte-gbw-wirtschaftskrimi-100.html'>Privatisierung von öffentlichem Wohnraum</a> und die <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/wirtschaft/sozialer-wohnungsbau-wird-immer-teurer-und-seltener,SeHOvK0'>hohen Kosten</a> für den sozialen Wohnungsbau."
                    data={[
                        { year: 2006, value: 2094 },
                        { year: 2007, value: 2034 },
                        { year: 2008, value: 1906 },
                        { year: 2009, value: 1806 },
                        { year: 2010, value: 1662 },
                        { year: 2011, value: 1491 },
                        { year: 2012, value: 1539 },
                        { year: 2013, value: 1475 },
                        { year: 2014, value: 0 },
                        { year: 2015, value: 1331 },
                        { year: 2016, value: 1269 },
                        { year: 2017, value: 1219 },
                        { year: 2018, value: 1176 },
                        { year: 2019, value: 1137 }
                    ]}
                    annotations={[
                        {
                            text: 'keine Daten',
                            year: 2014,
                            value: 1400
                        }
                    ]}
                    unit="Mio."
                    factor={1000}
                    precision={1}
                    maxValue={5000}
                    breakpoint={2008}
                    marginRight={90}
                    showHint={false}
                    totalScore={totalScore}
                    setTotalScore={setTotalScore}
                />

                <DrawIt
                    id="kitakinder"
                    question="Der Anteil der unter Dreijährigen, die in die Kita gehen ..."
                    answer="... hat sich seit 2006 fast verdreifacht. Im Jahr 2013, zu Beginn von Angela Merkels dritter Amtszeit, beschloss der Bundestag den <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/inland/hgkitaplatz100.html'>gesetzlichen Anspruch</a> auf einen Kitaplatz. Der seither stetige Zuwachs betreuter Kinder unter drei Jahren schwächte sich von 2019 auf 2020, dem ersten Jahr der Corona-Pandemie, etwas ab. Das derzeit größte Problem ist jedoch, dass es den Kindertagesstätten an <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/bayern/kitas-in-bayern-tausende-fachkraefte-fehlen,SboWcYz'>qualifiziertem Fachpersonal</a> mangelt."
                    data={[
                        { year: 2006, value: 253884 },
                        { year: 2007, value: 278642 },
                        { year: 2008, value: 313114 },
                        { year: 2009, value: 356274 },
                        { year: 2010, value: 400336 },
                        { year: 2011, value: 437390 },
                        { year: 2012, value: 472176 },
                        { year: 2013, value: 503926 },
                        { year: 2014, value: 561569 },
                        { year: 2015, value: 593639 },
                        { year: 2016, value: 614600 },
                        { year: 2017, value: 645077 },
                        { year: 2018, value: 665302 },
                        { year: 2019, value: 687427 },
                        { year: 2020, value: 695048 }
                    ]}
                    annotations={[
                        {
                            text: 'Rechtsanspruch',
                            color: '#000',
                            year: 2013,
                            value: 503926
                        }
                    ]}
                    unit="Tsd."
                    factor={1000}
                    precision={0}
                    maxValue={1000000}
                    breakpoint={2008}
                    marginRight={100}
                    showHint={false}
                    totalScore={totalScore}
                    setTotalScore={setTotalScore}
                />

                <DrawIt
                    id="asylantraege"
                    question="Die Zahl der Asylanträge ..."
                    answer="... stieg bereits seit 2011 nach dem Ausbruch des <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/deutschland-welt/syrien-10-jahre-nach-kriegsbeginn-der-stand-der-dinge,SP9s73N'>Bürgerkriegs in Syrien</a> merklich an und erreichte 2016 ihren <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/ausland/asylbewerber-149.html'>absoluten Höhepunkt</a>. Mehr als jeder vierte Antrag wurde damals in <a target='_blank' rel='noopener noreferrer' href='https://www.bpb.de/gesellschaft/migration/flucht/zahlen-zu-asyl/265765/abschiebungen'>Nordrhein-Westfalen</a> gestellt. Seitdem ging die Zahl der jährlichen Anträge fast auf das Niveau der Schröder-Jahre zurück. "
                    data={[
                        { year: 2002, value: 91471 },
                        { year: 2003, value: 67848 },
                        { year: 2004, value: 50152 },
                        { year: 2005, value: 42908 },
                        { year: 2006, value: 30100 },
                        { year: 2007, value: 30303 },
                        { year: 2008, value: 28018 },
                        { year: 2009, value: 33033 },
                        { year: 2010, value: 48589 },
                        { year: 2011, value: 53347 },
                        { year: 2012, value: 77651 },
                        { year: 2013, value: 127023 },
                        { year: 2014, value: 202834 },
                        { year: 2015, value: 476649 },
                        { year: 2016, value: 745545 },
                        { year: 2017, value: 222683 },
                        { year: 2018, value: 185853 },
                        { year: 2019, value: 165938 },
                        { year: 2020, value: 122170 }
                    ]}
                    annotations={[
                        {
                            text: 'Krieg in Syrien',
                            year: 2011,
                            value: 53347
                        }
                    ]}
                    unit="Tsd."
                    factor={1000}
                    precision={0}
                    maxValue={1000000}
                    breakpoint={2005}
                    marginRight={100}
                    showHint={false}
                    totalScore={totalScore}
                    setTotalScore={setTotalScore}
                />

                <DrawIt
                    id="hasskriminalität"
                    question="Wie hat sich die Zahl der Straftaten entwickelt, welche als Hasskriminalität eingestuft werden?"
                    description="Unter Hasskriminalität werden politisch motivierte Straftaten gegen Personen oder auch Institutionen aufgrund von Eigenschaften wie ihrer Nationalität, Hautfarbe, Religion oder sexuellen Orientierung zusammengefasst."
                    answer="Im Jahr 2015, dem <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/deutschland-welt/bka-chef-warnt-vor-zunahme-an-gewalt,64w30e9n6cu3ad1q74v30dtt64r38'>bisherigen Höhepunkt</a> der Flüchtlingskrise, verdoppelte sich die <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/inland/hintergrund-anschlaege-asyl-101.html'>Anzahl der Straftaten</a> annähernd und stieg im darauffolgenden Jahr ein weiteres Mal an. <a target='_blank' rel='noopener noreferrer' href='https://www.bmi.bund.de/SharedDocs/downloads/DE/veroeffentlichungen/2021/05/pmk-2020-uebersicht-hasskriminalitaet-entwicklung-fallzahlen.pdf;jsessionid=34E05E28B3EAA08B7ABD0AB7F4607213.1_cid295?__blob=publicationFile&v=2'>Laut Statistik</a> des Bundesinnenministeriums ist der starke Anstieg vor allem auf den <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/puls/themen/welt/mehr-rechtsextreme-gewalt-100.html'>Zuwachs rechtsradikaler Straftaten</a> zurückzuführen. Im Jahr 2020 erreichte die Zahl der Straftaten, auch in <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/inland/innenpolitik/corona-bka-hasskriminalitaet-101.html'>Zusammenhang mit den Corona-Protesten</a>, wieder ein ähnliches Niveau wie 2015 und 2016."
                    data={[
                        { year: 2002, value: 5013 },
                        { year: 2003, value: 3850 },
                        { year: 2004, value: 4114 },
                        { year: 2005, value: 4314 },
                        { year: 2006, value: 5353 },
                        { year: 2007, value: 4793 },
                        { year: 2008, value: 4757 },
                        { year: 2009, value: 4583 },
                        { year: 2010, value: 3770 },
                        { year: 2011, value: 4040 },
                        { year: 2012, value: 4514 },
                        { year: 2013, value: 4747 },
                        { year: 2014, value: 5858 },
                        { year: 2015, value: 10373 },
                        { year: 2016, value: 10751 },
                        { year: 2017, value: 7913 },
                        { year: 2018, value: 8113 },
                        { year: 2019, value: 8585 },
                        { year: 2020, value: 10240 }
                    ]}
                    annotations={[
                        {
                            text: 'Flüchtlingskrise',
                            year: 2015,
                            value: 10373
                        }
                    ]}
                    unit=""
                    factor={1}
                    precision={0}
                    maxValue={15000}
                    breakpoint={2005}
                    marginRight={80}
                    showHint={false}
                    totalScore={totalScore}
                    setTotalScore={setTotalScore}
                />

                <DrawIt
                    id="verteidigungsausgaben"
                    question="Wie haben sich die Verteidigungsausgaben entwickelt?"
                    answer="Seit 2015 steigen die Verteidigungsausgaben des Bundes stetig. Damit steuert die deutsche Bundesregierung zielstrebig auf das <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/inland/verteidigungsausgaben-103.html'>Zwei-Prozent-Ziel</a> zu. Dieses Ziel ist das Ergebnis eines NATO-Beschlusses, den Verteidigungsetat aller Mitgliedsstaaten auf zwei Prozent des Bruttoinlandsprodukts zu erhöhen. Trotz des gestiegenen Etats <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/deutschland-welt/kramp-karrenbauer-warnt-vor-neuen-bedrohungen-fuer-bundeswehr,SOcwwBq'>warnte Verteidigungsministerin Kramp-Karrenbauer</a> Anfang dieses Jahres vor einer weiterhin nicht ausreichenden Finanzierung der Bundeswehr. In Deutschland belaufen sich die Verteidigungsausgaben momentan auf <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/ausland/europa/nato-verteidigungsausgaben-103.html'>1,56 Prozent des Bruttoinlandsprodukts</a>. Die USA, Großbritannien, Frankreich und weitere Länder erreichten schon 2020 das Zwei-Prozent-Ziel."
                    data={[
                        { year: 2002, value: 29.338 },
                        { year: 2003, value: 29.237 },
                        { year: 2004, value: 28.813 },
                        { year: 2005, value: 24.385 },
                        { year: 2006, value: 28.604 },
                        { year: 2007, value: 29.307 },
                        { year: 2008, value: 30.788 },
                        { year: 2009, value: 32.054 },
                        { year: 2010, value: 32.487 },
                        { year: 2011, value: 32.488 },
                        { year: 2012, value: 34.088 },
                        { year: 2013, value: 33.322 },
                        { year: 2014, value: 33.664 },
                        { year: 2015, value: 34.418 },
                        { year: 2016, value: 36.039 },
                        { year: 2017, value: 37.62 },
                        { year: 2018, value: 39.331 },
                        { year: 2019, value: 43.774 },
                        { year: 2020, value: 46.291 }
                    ]}
                    annotations={[
                        {
                            text: 'Zwei-Prozent-Ziel',
                            year: 2015,
                            value: 33.664
                        }
                    ]}
                    unit="Mrd. €"
                    factor={1}
                    precision={0}
                    maxValue={50}
                    breakpoint={2005}
                    marginRight={100}
                    showHint={false}
                    totalScore={totalScore}
                    setTotalScore={setTotalScore}
                />
                {/* 
                <DrawIt
                    id="waffenexporte"
                    question="Die Waffenexporte in Euro ..."
                    answer="... sind zuletzt wieder zurückgegangen. Zuvor erreichte das <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/deutschland-welt/regierung-hat-ruestungsexporte-fuer-22-5-milliarden-genehmigt,ShSPKYZ'>jährliche Exportvolumen</a> in den Jahren 2015 während der Zuspitzung des Syrien-Konflikts und 2019 Spitzenwerte. Dabei legte der <a target='_blank' rel='noopener noreferrer' href='https://www.bmwi.de/Redaktion/DE/Downloads/B/bericht-der-bundesregierung-ueber-ihre-exportpolitik-fuer-konventionelle-ruestungsgueter-im-jahre-2020.html'>Export in EU-Länder</a> verglichen zu den beiden jeweiligen Vorjahren etwas stärker zu als in Drittländer. Hauptabnehmer waren 2019 unter anderem Ungarn, aber auch Drittländer wie Ägypten und die Vereinigten Arabischen Emirate, die sich im <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/inland/ruestungsexporte-deutschland-107.html'>Jemen-Krieg</a> aufseiten Saudi-Arabiens engagierten. Gegen das Land war nach der Ermordung des saudischen Journalisten Jamal Khashoggi ein <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/inland/ruestungsexporte-saudi-arabien-101.html'>Exportstopp</a> verhängt worden. Die Ausfuhr in NATO-Staaten oder ihrer Partnerländer erreichte 2016 ihren <a target='_blank' rel='noopener noreferrer' href='https://www.bmwi.de/Redaktion/DE/Downloads/B/bericht-der-bundesregierung-ueber-ihre-exportpolitik-fuer-konventionelle-ruestungsgueter-im-jahre-2020.html'>höchsten Wert</> seit Merkels Amtsantritt."
                    data={[
                        { year: 2005, value: 4216 },
                        { year: 2006, value: 4188 },
                        { year: 2007, value: 3668 },
                        { year: 2008, value: 5788 },
                        { year: 2009, value: 5043 },
                        { year: 2010, value: 4754 },
                        { year: 2011, value: 5414 },
                        { year: 2012, value: 4704 },
                        { year: 2013, value: 5846 },
                        { year: 2014, value: 3961 },
                        { year: 2015, value: 7859 },
                        { year: 2016, value: 6848 },
                        { year: 2017, value: 6242 },
                        { year: 2018, value: 4824 },
                        { year: 2019, value: 8014 },
                        { year: 2020, value: 5824 }
                    ]}
                    unit="Mrd. €"
                    factor={1000}
                    precision={1}
                    maxValue={10000}
                    breakpoint={2007}
                    marginRight={110}
                    showHint={false}
                    totalScore={totalScore}
                    setTotalScore={setTotalScore}
                /> */}

                <DrawIt
                    id=""
                    question="Der Anteil der Kernenergie an der Stromerzeugung ..."
                    answer="... sinkt seit 2009 <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/deutschland-welt/atomausstieg-wind-wasser-und-sonne-auf-dem-vormarsch,SRKJr2E'>stetig</a>. 2011, nach der <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/ausland/asien/japan-gedenken-fukushima-101.html'>Reaktorkatastrophe im japanischen Fukushima</a>, beschloss das Bundeskabinett den <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/inland/atomausstieg208.html'>endgültigen Ausstieg</a> aus der Atomenergie bis 2022. Einige <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/wirtschaft/klage-atomausstieg-101.html'>Kraftwerksbetreiber klagten</a> gegen den Atomausstieg und bekamen vor dem Bundesverfassungsgericht <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/wirtschaft/vattenfall-klage-atomausstieg-101.html'>teilweise Recht</a>. Die Bundesregierung einigte sich mit den Betreibern auf Entschädigungszahlungen <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/wirtschaft/unternehmen/atomausstieg-entschaedigung-101.html'>in Milliardenhöhe</a>. In der letzten Legislaturperiode von Angela Merkel ging der Atomausstieg jedoch eher langsam voran und der Anteil der Kernenergie verblieb bei knapp elf Prozent."
                    data={[
                        { year: 2002, value: 29.9 },
                        { year: 2003, value: 28.1 },
                        { year: 2004, value: 32.1 },
                        { year: 2005, value: 26.6 },
                        { year: 2006, value: 31.8 },
                        { year: 2007, value: 25.9 },
                        { year: 2008, value: 28.3 },
                        { year: 2009, value: 26.1 },
                        { year: 2010, value: 22.6 },
                        { year: 2011, value: 17.8 },
                        { year: 2012, value: 16.1 },
                        { year: 2013, value: 15.5 },
                        { year: 2014, value: 15.9 },
                        { year: 2015, value: 14.1 },
                        { year: 2016, value: 13.1 },
                        { year: 2017, value: 11.6 },
                        { year: 2018, value: 11.7 },
                        { year: 2019, value: 11.5 },
                        { year: 2020, value: 11.3 }
                    ]}
                    annotations={[
                        {
                            text: 'Fukushima',
                            year: 2011,
                            value: 17.8
                        }
                    ]}
                    unit="%"
                    factor={1}
                    precision={1}
                    maxValue={50}
                    breakpoint={2005}
                    showHint={false}
                    totalScore={totalScore}
                    setTotalScore={setTotalScore}
                />

                <DrawIt
                    id="energiemix"
                    question="Der Anteil erneuerbarer Energien beim Strom ..."
                    answer="... steigt weiter an. Zu Beginn von Merkels erster Kanzlerschaft lag deren Anteil am Bruttostromverbrauch, also dem bundesweiten Endverbrauch an Energie inklusive des Eigenverbrauchs, etwa von Heizwerken bei der Umwandlung in Wärme, noch bei überschaubaren 10 Prozent. Bis zum vergangenen Jahr <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/deutschland-welt/atomausstieg-wind-wasser-und-sonne-auf-dem-vormarsch,SRKJr2E'>vervierfachte sich der Wert</a> und übertraf so die für 2020 vom Bund angestrebten 35 Prozent bei weitem. "
                    data={[
                        { year: 2002, value: 7.7 },
                        { year: 2003, value: 7.7 },
                        { year: 2004, value: 9.4 },
                        { year: 2005, value: 10.3 },
                        { year: 2006, value: 11.6 },
                        { year: 2007, value: 14.3 },
                        { year: 2008, value: 15.2 },
                        { year: 2009, value: 16.4 },
                        { year: 2010, value: 17.0 },
                        { year: 2011, value: 20.4 },
                        { year: 2012, value: 23.5 },
                        { year: 2013, value: 25.1 },
                        { year: 2014, value: 27.4 },
                        { year: 2015, value: 31.5 },
                        { year: 2016, value: 31.6 },
                        { year: 2017, value: 36.0 },
                        { year: 2018, value: 37.8 },
                        { year: 2019, value: 42.0 },
                        { year: 2020, value: 45.0 }
                    ]}
                    unit="%"
                    factor={1}
                    precision={0}
                    maxValue={75}
                    breakpoint={2005}
                    showHint={false}
                    totalScore={totalScore}
                    setTotalScore={setTotalScore}
                />

                <DrawIt
                    id="windkraft"
                    question="Wie viel Windkraftanlagen werden jedes Jahr neu gebaut?"
                    answer="Der Ausbau der Windenergie in Deutschland hat <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/deutschland-welt/trotz-anlagen-plus-ausbau-der-windkraft-kommt-nur-langsam-voran,SeMFFBP'>an Tempo verloren</a>. Während der Bau von Windenergieanlagen auf dem Festland vor allem zwischen 2010 und 2017 Konjunktur hatte, stagniert der Zubau seit einigen Jahren. Gründe dafür sind vor allem die <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/wirtschaft/windkraft-bayern-baut-kaum-neue-anlagen,SNBAzlL'>Widerstände von Anwohnern</a> sowie der jahrelange Streit der Großen Koalition über eine mögliche <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/wirtschaft/windkraft-abstandsregel-101.html'>bundesweite Abstandsregelung</a>. Auch die <a target='_blank' rel='noopener noreferrer' href='https://www.dw.com/de/dramatischer-einbruch-beim-windausbau-was-l%C3%A4uft-schief-in-deutschland-eeg-windkraft-erneuerbare/a-49076585'>Gesetzesänderung zum Wettbewerbsverfahren</a> beim Ausbau 2016 bremste die Windkraft in Deutschland stark aus. Im Mai vergangenen Jahres <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/inland/windraeder-abstand-101.html'>einigte man sich</a>, die Entscheidungshoheit über die Mindestabstände bei <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/bayern/windkraft-soeder-haelt-an-10h-regel-fest-aber-mehr-ausnahmen,Sdma0Cc'>den Ländern</a> zu belassen."
                    data={[
                        { year: 2002, value: 2328 },
                        { year: 2003, value: 1703 },
                        { year: 2004, value: 1201 },
                        { year: 2005, value: 1049 },
                        { year: 2006, value: 1208 },
                        { year: 2007, value: 883 },
                        { year: 2008, value: 866 },
                        { year: 2009, value: 952 },
                        { year: 2010, value: 754 },
                        { year: 2011, value: 895 },
                        { year: 2012, value: 998 },
                        { year: 2013, value: 1154 },
                        { year: 2014, value: 1766 },
                        { year: 2015, value: 1368 },
                        { year: 2016, value: 1624 },
                        { year: 2017, value: 1792 },
                        { year: 2018, value: 743 },
                        { year: 2019, value: 325 },
                        { year: 2020, value: 420 }
                    ]}
                    annotations={[
                        {
                            text: 'Gesetzesänderung',
                            year: 2016,
                            value: 1624
                        }
                    ]}
                    unit=""
                    factor={1}
                    precision={0}
                    maxValue={2500}
                    breakpoint={2005}
                    showHint={false}
                    totalScore={totalScore}
                    setTotalScore={setTotalScore}
                />

                <DrawIt
                    id="elektroautos"
                    question="Der Anteil von Elektrofahrzeugen an allen Neuzulassungen pro Jahr  ..."
                    answer="... hat mit jedem Maßnahmenpaket der Bundesregierung unter Angela Merkel <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/wirtschaft/elektroautos-in-deutschland-bald-eine-million,SbzoM1J'>zugelegt</a>. Zwar lag der prozentuale Anteil nach der ersten Förderung in Form des sogenannten <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/wirtschaft/e-autos-109.html'>Umweltbonus</a> immer noch unter einem Prozent, doch konnte mit den Erhöhungen der Förderbeteiligung ab 2018 ein <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/wirtschaft/technologie/elektroauto-foerderung-umweltpraemie-bafa-altmaier-101.html'>deutlicher Zuwachs</a> verzeichnet werden. In der ersten Hälfte des laufenden Jahres hatte bereits mehr als <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/wirtschaft/konjunktur/verbrenner-elektrofahrzeuge-neuzulassungen-kba-101.html'>jeder fünfte</a> neuzugelassene Wagen einen Elektroantrieb."
                    data={[
                        { year: 2011, value: 0.07 },
                        { year: 2012, value: 0.1 },
                        { year: 2013, value: 0.2 },
                        { year: 2014, value: 0.28 },
                        { year: 2015, value: 0.39 },
                        { year: 2016, value: 0.34 },
                        { year: 2017, value: 0.73 },
                        { year: 2018, value: 1.05 },
                        { year: 2019, value: 1.75 },
                        { year: 2020, value: 6.65 }
                    ]}
                    annotations={[
                        {
                            text: 'Umweltbonus',
                            year: 2016,
                            value: 0.34
                        }
                    ]}
                    unit="%"
                    factor={1}
                    precision={0}
                    maxValue={15}
                    breakpoint={2013}
                    showHint={false}
                    totalScore={totalScore}
                    setTotalScore={setTotalScore}
                />

                <DrawIt
                    id="frauen-dax"
                    question="Wie hoch ist der Frauenanteil unter den Vorstandsmitgliedern deutscher DAX-Konzerne?"
                    answer="Der Frauenanteil in den Vorständen der DAX-Unternehmen <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/wirtschaft/vorstaende-werden-weiblicher-zumindest-ein-bisschen,SMSGigZ'> hat auf niedrigem Niveau zugelegt</a>. Erst im Juni dieses Jahres <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/wirtschaft/unternehmen/frauen-vorstaende-dax-konzerne-101.html'>beschloss der Bundestag</a> mit Wirkung zum 11. August, dass mindestens eine Frau in jedem DAX-Vorstand mit mehr als drei Mitgliedern sitzen muss. Die Riege der Vorstandsvorsitzenden allerdings bleibt mit nur knapp 5 Prozent Frauenanteil noch weit mehr <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/wirtschaft/einige-dax-firmen-verfehlen-die-frauenquote-101.html'>von Männern beherrscht</a>."
                    data={[
                        { year: 2011, value: 3.5 },
                        { year: 2012, value: 5.6 },
                        { year: 2013, value: 6.2 },
                        { year: 2014, value: 5.4 },
                        { year: 2015, value: 5.8 },
                        { year: 2016, value: 6.7 },
                        { year: 2017, value: 7.2 },
                        { year: 2018, value: 8.2 },
                        { year: 2019, value: 9.3 },
                        { year: 2020, value: 11.1 }
                    ]}
                    unit="%"
                    factor={1}
                    precision={1}
                    maxValue={50}
                    breakpoint={2013}
                    marginRight={80}
                    showHint={false}
                    totalScore={totalScore}
                    setTotalScore={setTotalScore}
                />

                <DrawIt
                    id="internetnutzer"
                    question="Der Anteil der Internetnutzer in der Bevölkerung ..."
                    answer="... ist immer weiter gestiegen. Einen großen Anteil daran hat mittlerweile auch die Nutzung des mobilen Internets <a target='_blank' rel='noopener noreferrer' href='https://initiatived21.de/app/uploads/2021/02/d21-digital-index-2020_2021.pdf'>mit Smartphones und Tablets</a>. Ein großes Streitthema ist jedoch weiterhin der <a target='_blank' rel='noopener noreferrer' href='https://www.br.de/nachrichten/deutschland-welt/stadt-land-gefaelle-in-deutschland-weiterhin-gross,SG2vROp'>flächendeckende Breitbandausbau</a>, gerade in ländlichen Regionen.<br><br>Wie lange Angela Merkel schon im Amt ist, zeigt auch ihre eigene Antwort zum Stand der Digitalisierung in Deutschland bei ihrer <a target='_blank' rel='noopener noreferrer' href='https://www.tagesschau.de/inland/merkel-befragung-corona-101.html'>letzten Regierungsbefragung im Bundestag</a>: „Als ich Bundeskanzlerin wurde, gab es das iPhone noch nicht.“"
                    data={[
                        { year: 2002, value: 41.7 },
                        { year: 2003, value: 50.1 },
                        { year: 2004, value: 52.7 },
                        { year: 2005, value: 55.1 },
                        { year: 2006, value: 58.2 },
                        { year: 2007, value: 60.2 },
                        { year: 2008, value: 65.1 },
                        { year: 2009, value: 69.1 },
                        { year: 2010, value: 72.0 },
                        { year: 2011, value: 74.7 },
                        { year: 2012, value: 75.6 },
                        { year: 2013, value: 76.5 },
                        { year: 2014, value: 76.8 },
                        { year: 2015, value: 77.6 },
                        { year: 2016, value: 79.0 },
                        { year: 2017, value: 81.0 },
                        { year: 2018, value: 84.0 },
                        { year: 2019, value: 86.0 },
                        { year: 2020, value: 88.0 }
                    ]}
                    unit="%"
                    factor={1}
                    precision={0}
                    maxValue={100}
                    breakpoint={2005}
                    showHint={false}
                    totalScore={totalScore}
                    setTotalScore={setTotalScore}
                />
            </Section>

            <Section>
                <Block>
                    <DrawItResult totalScore={totalScore} totalCount={13} />
                </Block>
            </Section>

            <Section>
                <Block>
                    <InfoBox isCollapsible={true}>
                        <P>
                            <Strong>Redaktion:</Strong> Pia Dangelmayer
                            <br />
                            <Strong>Mitarbeit:</Strong> Sebastian Bayerl, Mira Dönges
                            <br />
                            <Strong>Inspiration:</Strong>{' '}
                            <A href="https://www.nytimes.com/interactive/2017/01/15/us/politics/you-draw-obama-legacy.html">
                                You Draw It: Obama’s Legacy
                            </A>
                        </P>

                        <H2>Quellen:</H2>

                        <List>
                            <ListItem>
                                Arbeitslosenquote:{' '}
                                <A href="https://www.destatis.de/DE/Themen/Arbeit/Arbeitsmarkt/Erwerbslosigkeit/Tabellen/lrarb002.html">
                                    Statistisches Bundesamt
                                </A>
                            </ListItem>
                            <ListItem>
                                Staatsverschuldung:{' '}
                                <A href="https://www.bundesbank.de/dynamic/action/de/statistiken/zeitreihen-datenbanken/zeitreihen-datenbank/723452/723452?tsId=BBK01.BJ9059&dateSelect=2020">
                                    Deutsche Bundesbank
                                </A>
                            </ListItem>
                            <ListItem>
                                Sozialwohnungen:{' '}
                                <A href="https://de.statista.com/statistik/daten/studie/892789/umfrage/sozialwohnungen-in-deutschland/">
                                    Statista (Kleine Anfrage)
                                </A>
                            </ListItem>
                            <ListItem>
                                Kitaplätze:{' '}
                                <A href="https://www-genesis.destatis.de/genesis/online?sequenz=tabelleErgebnis&selectionname=22541-0001#abreadcrumb">
                                    Statistisches Bundesamt
                                </A>
                            </ListItem>
                            <ListItem>
                                Asylanträge:{' '}
                                <A href="https://www.bamf.de/DE/Themen/Statistik/Asylzahlen/AktuelleZahlen/aktuellezahlen-node.html">
                                    Bundesamt für Migration und Flüchtlinge
                                </A>
                            </ListItem>
                            <ListItem>
                                Hasskriminalität:{' '}
                                <A href="https://www.bmi.bund.de/SharedDocs/downloads/DE/veroeffentlichungen/2021/05/pmk-2020-bundesweite-fallzahlen.pdf?__blob=publicationFile&v=4">
                                    Bundesinnenministerium
                                </A>
                            </ListItem>
                            <ListItem>
                                Verteidigungsausgaben:{' '}
                                <A href="https://sipri.org/databases/milex">
                                    Stockholm International Peace Research Institute
                                </A>
                            </ListItem>
                            <ListItem>
                                Waffenexporte:{' '}
                                <A href="https://www.bmwi.de/Redaktion/DE/Downloads/B/bericht-der-bundesregierung-ueber-ihre-exportpolitik-fuer-konventionelle-ruestungsgueter-im-jahre-2020.html">
                                    Bundeswirtschaftsministerium
                                </A>
                            </ListItem>
                            <ListItem>
                                Kernenergie:{' '}
                                <A href="https://de.statista.com/statistik/daten/studie/29295/umfrage/anteil-der-atomenergie-an-der-stromerzeugung-in-deutschland/">
                                    Statista (IAEA)
                                </A>
                            </ListItem>
                            <ListItem>
                                Erneuerbare Energien:{' '}
                                <A href="https://www.umweltbundesamt.de/daten/energie/erneuerbare-konventionelle-stromerzeugung#bruttostromerzeugung-nach-energietragern-">
                                    Umweltbundesamt
                                </A>
                            </ListItem>
                            <ListItem>
                                Windkraftanlagen:{' '}
                                <A href="https://www.wind-energie.de/themen/zahlen-und-fakten/deutschland/">
                                    Bundesverband WindEnergie e.V.
                                </A>
                            </ListItem>
                            <ListItem>
                                Elektrofahrzeugen:{' '}
                                <A href="https://www.kba.de/DE/Statistik/Fahrzeuge/Neuzulassungen/Umwelt/fz_n_umwelt_archiv/2020/n_umwelt_z.html?nn=2601598">
                                    Kraftfahrtbundesamt
                                </A>
                            </ListItem>
                            <ListItem>
                                Frauenanteil DAX:{' '}
                                <A href="https://www.diw.de/documents/publikationen/73/diw_01.c.808778.de/21-3.pdf">
                                    Deutsches Institut für Wirtschaftsforschung
                                </A>
                            </ListItem>
                            <ListItem>
                                Internetnutzer:{' '}
                                <A href="https://de.statista.com/statistik/daten/studie/13070/umfrage/entwicklung-der-internetnutzung-in-deutschland-seit-2001/">
                                    Statista (Initiative D21)
                                </A>
                            </ListItem>
                        </List>
                    </InfoBox>
                </Block>
            </Section>

            <Section>
                <Block>
                    <LazyLoadComponent>
                        <H3 style={{ textTransform: 'uppercase' }}>Das könnte Sie auch interessieren:</H3>
                        <RelatedContent>
                            <Teaser
                                title="BR24: Bundestagswahl 2021"
                                text="Nachrichten, Analysen und Faktenchecks zur Bundestagswahl 2021. Alle Hochrechnungen und Ergebnisse live in der Wahlnacht am 26. September."
                                href="https://www.br.de/nachrichten/alles-zur-bundestagswahl-2021,S8YieJ6"
                                img={
                                    'https://img.br.de/129b25ab-cf51-467e-abd5-72d7e3afa541.jpeg?q=80&rect=0%2C0%2C1918%2C1080&w=1800'
                                }
                                alt="BR24 Wahlportal"
                            />
                            <Teaser
                                title="BR24: Parteien, Themen und Kandiaten"
                                text="Bundestagswahl 2021 in Bayern: Welche Partei steht für was? Wie sind die Positionen zu den wichtigsten Themen? Welche Spitzenkandidaten gehen ins Rennen?"
                                href="https://www.br.de/nachrichten/bundestagswahl-2021-parteien-themen-kandidaten,SfaZQ60"
                                img={
                                    'https://img.br.de/3a49b5df-f363-47a2-bddb-86a9269ba1c8.jpeg?q=80&rect=0%2C0%2C1918%2C1080&w=1800'
                                }
                                alt="BR Mediathek"
                            />
                            <Teaser
                                timestamp="2.7.2021"
                                title="BR24 Podcast: Angela Merkels Abschied"
                                text="Die Lotsin geht von Bord - Angela Merkels Abschied von Europa. Eine Reportage über die Ära Merkel aus EU-Perspektive und über das Vermächtnis der Kanzlerin."
                                href="https://www.br.de/mediathek/podcast/br24-reportage/die-lotsin-geht-von-bord-angela-merkels-abschied-von-europa/1830991"
                                img={
                                    'https://www.br.de/radio/br24-reportage-100~_v-img__16__9__xl_-d31c35f8186ebeb80b0cd843a7c267a0e0c81647.png?version=5faa0'
                                }
                                alt="Tagesticket"
                            />
                        </RelatedContent>
                    </LazyLoadComponent>
                </Block>
            </Section>
        </Article>
    );
};
